/**
 * Hook for fetching affaire context data
 * Used by AffaireContextSidebar when navigating from Tracker to Facturation
 */

import { useQuery } from '@tanstack/react-query';
import { searchAPI } from '../../search/api/search.api';
import { useAffairePaymentSummary } from '../../tracker/hooks/useAffairePaymentSummary';
import type { Affaire } from '../../search/types/search.types';

interface AffaireContext {
  affaire: Affaire | null;
  paymentSummary: ReturnType<typeof useAffairePaymentSummary>['data'] | undefined;
  isLoading: boolean;
  error: Error | null;
}

/**
 * Fetch affaire context data for sidebar display
 * Combines basic affaire info with payment summary
 *
 * @param affaireId - The affaire ID (number or string)
 * @returns Object with affaire data, payment summary, loading state, and error
 */
export const useAffaireContext = (affaireId: number | string | null): AffaireContext => {
  const affaireIdNum = affaireId ? (typeof affaireId === 'string' ? parseInt(affaireId, 10) : affaireId) : null;
  const affaireIdStr = affaireId ? String(affaireId) : null;

  // Fetch basic affaire data
  const {
    data: affaireData,
    isLoading: affaireLoading,
    error: affaireError,
  } = useQuery({
    queryKey: ['affaire-context', affaireIdStr],
    queryFn: async () => {
      if (!affaireIdStr) return null;
      const response = await searchAPI.searchAffaire(affaireIdStr);
      if (response.success && response.data.affaire) {
        return response.data.affaire;
      }
      return null;
    },
    enabled: !!affaireIdStr,
    staleTime: 60000, // 1 minute
    refetchOnWindowFocus: false,
  });

  // Fetch payment summary
  const { data: paymentSummary, isLoading: paymentLoading } = useAffairePaymentSummary(affaireIdNum);

  return {
    affaire: affaireData || null,
    paymentSummary,
    isLoading: affaireLoading || paymentLoading,
    error: affaireError as Error | null,
  };
};
